import { useMemo } from 'react';
import { useApplicationCredentials } from '../useApplicationCredentials';

type AuthenticationInfoData = {
  credentials: {
    total: number;
    verified: number;
    pending: number;
    verifying: number;
    failed: number;
    disabled: number;
  };
  roles: Array<{
    name: string;
    userCount: number;
  }>;
  authMethods: {
    userPass: number;
    bearer: number;
    sso: number;
    none: number;
  };
};

export function useAuthenticationInfo(applicationId: string) {
  const { credentials, isLoading } = useApplicationCredentials({
    applicationId,
    enabled: !!applicationId,
  });

  const data = useMemo((): AuthenticationInfoData | null => {
    if (isLoading) return null;

    const credentialCounts = credentials.reduce(
      (acc, cred) => {
        if (cred.isDisabled) {
          acc.disabled += 1;
          return acc;
        }
        const status = cred.verification?.status;
        if (status === 'VERIFIED') acc.verified += 1;
        else if (status === 'VERIFYING') acc.verifying += 1;
        else if (status === 'FAILED') acc.failed += 1;
        else acc.pending += 1; // PENDING or missing verification
        return acc;
      },
      { verified: 0, pending: 0, verifying: 0, failed: 0, disabled: 0 },
    );

    const roleUserCounts: Record<string, number> = {};
    credentials.forEach((cred) => {
      const roleName = cred.role || 'Unknown';
      roleUserCounts[roleName] = (roleUserCounts[roleName] || 0) + 1;
    });

    const roles = Object.entries(roleUserCounts).map(([name, userCount]) => ({
      name,
      userCount,
    }));

    let bearerCount = 0;
    credentials.forEach((cred) => {
      const hasBearer =
        cred.headers.some((h) =>
          h.key.toLowerCase().includes('authorization'),
        ) || cred.secrets.some((s) => s.key.toLowerCase().includes('bearer'));
      if (hasBearer) bearerCount++;
    });

    const authMethods = {
      userPass: credentials.length - bearerCount,
      bearer: bearerCount,
      sso: 0,
      none: 0,
    };

    return {
      credentials: {
        total: credentials.length,
        ...credentialCounts,
      },
      roles,
      authMethods,
    };
  }, [credentials, isLoading]);

  return {
    data,
    isLoading,
  };
}
